import * as React from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';

import type { RankFreezeVersionRow } from '@/api/rank-admin-client';
import { useListFreezes } from '@/api/use-rank-admin';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';

// =============================================================================
// rank-frozen-versions-panel.tsx — Panneau ADMIN « Versions figées du livrable » (V26-DELIVERABLE-EXPOSE
// / TRANCHE A, S20-REMEDIATION D1-B3 étendu).
// =============================================================================
// DBU §2.6 : le livrable client EST un fichier XLSX, par version horodatée, conservé (historique des
// figements). Ce panneau consomme le read-model D1-B3 (`GET /admin/positioning/:campaignId/freezes`) via
// le RankAdminClient et affiche, pour chaque version : numéro, instant de figement, taille du panel,
// post-publication, et la PRÉSENCE du livrable XLSX. AUCUN lien de téléchargement (TRANCHE B, différée —
// dépend d'un GcsReadPort analytics non câblé, cf. cadrage ARCH). ZÉRO règle métier : lecture pure.
//
// ⚠️ LOT A3 — la colonne PDF est SUPPRIMÉE (décision GATE_HUMAN du 2026-09-17 : « il faut garder "Figer le
// panel" et l'historique des versions figées. Il ne faut pas garder le PDF. »). Le figement et
// l'historique des versions RESTENT ; le livrable client se produit depuis l'export de la page. Le
// nettoyage complet de l'écran relève du lot U1 (page fusionnée) : ici, strict minimum de cohérence.
//
// WCAG 2.1 AA : <table>/<caption>/<th scope>, présence du livrable DOUBLÉE libellé texte + icône
// (jamais la couleur seule, contraste ≥ 4.5:1 — parité RankBadge de rank-working-table-screen.tsx).

export interface FrozenVersionsPanelProps {
  readonly campaignId: string;
}

export function FrozenVersionsPanel({
  campaignId,
}: FrozenVersionsPanelProps): React.JSX.Element {
  const query = useListFreezes(campaignId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Versions figées du livrable</CardTitle>
        <CardDescription>
          Historique des figements du panel (livrable client XLSX, DBU §2.6). Le
          téléchargement des fichiers depuis cet écran n’est pas encore disponible.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <p role="status" className="text-muted-foreground">
            Chargement des versions figées…
          </p>
        ) : query.isError ? (
          <p role="alert" className="text-destructive">
            Erreur de chargement : {query.error.message}
          </p>
        ) : query.data.length === 0 ? (
          <p role="status" className="text-muted-foreground">
            Aucune version figée pour cette campagne.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="ds-table w-full border-collapse text-sm">
              <caption className="sr-only">
                Versions figées du livrable : numéro, date de figement, taille du panel,
                post-publication, disponibilité du XLSX.
              </caption>
              <thead>
                <tr className="border-b border-border">
                  <th
                    scope="col"
                    className="px-3 py-2 text-left font-medium text-muted-foreground"
                  >
                    Version
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left font-medium text-muted-foreground"
                  >
                    Figée le
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left font-medium text-muted-foreground"
                  >
                    Taille du panel
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left font-medium text-muted-foreground"
                  >
                    Post-publication
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left font-medium text-muted-foreground"
                  >
                    XLSX
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((row) => (
                  <FreezeVersionRow key={row.versionNumber} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FreezeVersionRow({
  row,
}: {
  readonly row: RankFreezeVersionRow;
}): React.JSX.Element {
  return (
    <tr className="border-b border-border">
      <th scope="row" className="px-3 py-2 text-left font-medium">
        v{String(row.versionNumber)}
      </th>
      <td className="px-3 py-2">{formatFrozenAt(row.frozenAt)}</td>
      <td className="px-3 py-2">{String(row.panelSize)}</td>
      <td className="px-3 py-2">{row.postPublication ? 'Oui' : 'Non'}</td>
      <td className="px-3 py-2">
        <DeliverableBadge available={row.hasXlsx} formatLabel="XLSX" />
      </td>
    </tr>
  );
}

/**
 * Badge de présence du livrable — DOUBLE le renfort couleur d'un libellé texte explicite
 * (« XLSX disponible » / « XLSX absent ») + une icône distincte (jamais la couleur seule, WCAG 2.1 AA).
 */
function DeliverableBadge({
  available,
  formatLabel,
}: {
  readonly available: boolean;
  readonly formatLabel: string;
}): React.JSX.Element {
  const Icon = available ? CheckCircle2 : XCircle;
  const className = available
    ? 'border-emerald-300 bg-emerald-100 text-emerald-900'
    : 'border-neutral-400 bg-neutral-200 text-neutral-900';
  const label = available ? `${formatLabel} disponible` : `${formatLabel} absent`;
  return (
    <Badge variant="outline" className={cn('gap-1', className)}>
      <Icon aria-hidden="true" className="h-3.5 w-3.5" />
      <span>{label}</span>
    </Badge>
  );
}

/** Formate un instant de figement ISO en date lisible (fr-FR, jour). Fail-safe : renvoie l'ISO si invalide. */
function formatFrozenAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
